import { IconFunctionalSuccessCircle16 } from '@cian/ui-kit/icons';
import { FC, ReactNode } from 'react';

import picAvatar from 'shared/components/SuperhostPage/assets/pic-avatar.png';
import picDocs from 'shared/components/SuperhostPage/assets/pic-docs.png';
import picLike from 'shared/components/SuperhostPage/assets/pic-like.png';
import picPlane from 'shared/components/SuperhostPage/assets/pic-plane.png';
import superagentIcon from 'shared/components/SuperhostPage/assets/superagent.svg';

import style from './Criteria.css';

interface IMetricsStep {
  type: 'metrics';
  title?: string;
  label: string;
  description: ReactNode;
  note?: string;
  showQualityBar: boolean;
  metrics: { label: string; value: string }[];
}

interface ITask {
  text: string;
  image: string;
  imageScale?: number;
}

interface ITasksStep {
  type: 'tasks';
  label: string;
  description: ReactNode;
  tasks: ITask[];
}

type TStep = IMetricsStep | ITasksStep;

interface ITabData {
  steps: TStep[];
}

const TABS_DATA: ITabData[] = [
  // Собственнику
  {
    steps: [
      {
        type: 'metrics',
        label: 'Шаг 1',
        description: 'Продвигайтесь по шкале уровня сервиса, чтобы достичь зелёной зоны',
        showQualityBar: true,
        metrics: [
          { label: 'Частота ответов', value: '>60%' },
          { label: 'Серьёзные нарушения', value: '<1%' },
          { label: 'Доля отмен бронирований', value: '<10%' },
          { label: 'Общий рейтинг', value: '>4' },
        ],
      },
      {
        type: 'tasks',
        label: 'Шаг 2',
        description: 'Продвигайтесь по шкале уровня сервиса, чтобы достичь зелёной зоны',
        tasks: [
          { text: 'Заполните ФИО\nв вашем профиле', image: picAvatar },
          { text: 'Получите 3 отзыва на каждый объект', image: picLike },
          { text: 'Вступайте в чат\nв MAX', image: picPlane, imageScale: 1.15 },
        ],
      },
    ],
  },
  // Агентству или УК (посуточно)
  {
    steps: [
      {
        type: 'metrics',
        label: 'Шаг 1',
        description: 'Продвигайтесь по шкале уровня сервиса, чтобы достичь зелёной зоны',
        showQualityBar: true,
        metrics: [
          { label: 'Частота ответов', value: '>60%' },
          { label: 'Серьёзные нарушения', value: '<1%' },
          { label: 'Доля отмен бронирований', value: '<10%' },
          { label: 'Общий рейтинг', value: '>4' },
        ],
      },
      {
        type: 'tasks',
        label: 'Шаг 2',
        description: 'Продвигайтесь по шкале уровня сервиса, чтобы достичь зелёной зоны',
        tasks: [
          { text: 'Заполните ФИО\nв вашем профиле', image: picAvatar },
          { text: 'Получите 3 отзыва на каждый объект', image: picLike },
          { text: 'Вступайте в чат\nв MAX', image: picPlane, imageScale: 1.15 },
        ],
      },
    ],
  },
  // Агентству или УК (посуточно и вторично)
  {
    steps: [
      {
        type: 'metrics',
        title: 'Суперагент',
        label: 'Шаг 1',
        description: (
          <>
            Для <span className={style['card-description--bold']}>вторичной недвижимости</span> двигайтесь по шкале
            уровня сервиса
          </>
        ),
        note: 'в зелёной зоне — статус Суперагент, самый высокий уровень',
        showQualityBar: true,
        metrics: [
          { label: 'Общение с клиентами', value: '· Отлично' },
          { label: 'Частота ответов', value: '>80%' },
          { label: 'Серьёзные нарушения', value: '<1%' },
        ],
      },
      {
        type: 'metrics',
        label: 'Шаг 2',
        title: 'Суперагент',
        description: (
          <>
            Для <span className={style['card-description--bold']}>посуточной аренды</span> отдельный раздел с
            дополнительными критериями — статус Суперхозяин
          </>
        ),
        showQualityBar: false,
        metrics: [
          { label: 'Доля отмен бронирований', value: '<10%' },
          { label: 'Общий рейтинг', value: '>4' },
        ],
      },
      {
        type: 'tasks',
        label: 'Шаг 3',
        description: 'Выполните специальные задания',
        tasks: [
          { text: 'Разместите ваш\nпрофиль в каталоге', image: picDocs, imageScale: 2 },
          { text: 'Получите 3 отзыва\nна каждый объект', image: picLike },
          { text: 'Вступите в чат\nв MAX', image: picPlane, imageScale: 1.15 },
        ],
      },
    ],
  },
];

interface ICriteriaContentProps {
  activeTab: number;
}

export const CriteriaContent: FC<ICriteriaContentProps> = ({ activeTab }) => {
  const data = TABS_DATA[activeTab];

  return (
    <div className={style['content']}>
      {data.steps.map(step => (
        <div key={step.label} className={style['card']}>
          <div className={style['card-header']}>
            <span className={style['step-label']}>{step.label}</span>
            <p className={style['card-description']}>{step.description}</p>
            {step.type === 'metrics' && step.note && (
              <p className={style['card-note']}>
                <span className={style['card-note-border']} />
                {step.note}
              </p>
            )}
          </div>

          {step.type === 'metrics' && (
            <div className={style['metrics-card']}>
              <div className={style['metrics-header']}>
                <img alt="" className={style['metrics-icon']} src={superagentIcon} />
                <p className={style['metrics-title']}>{step.title || 'Суперхозяин'}</p>
              </div>
              {step.showQualityBar && (
                <div className={style['quality-bar']}>
                  <div className={style['quality-red']} />
                  <div className={style['quality-orange']} />
                  <div className={style['quality-light-green']} />
                  <div className={style['quality-green']} />
                </div>
              )}
              <div className={style['metrics-list']}>
                {step.metrics.map(metric => (
                  <div key={metric.label} className={style['metric']}>
                    <IconFunctionalSuccessCircle16 color="accent-positive-primary" />
                    <p className={style['metric-label']}>{metric.label}</p>
                    <p className={style['metric-value']}>{metric.value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step.type === 'tasks' && (
            <div className={style['tasks-list']}>
              {step.tasks.map(task => (
                <div key={task.text} className={style['task-card']}>
                  <img
                    alt=""
                    className={style['task-image']}
                    src={task.image}
                    style={task.imageScale ? { transform: `scale(${task.imageScale})` } : undefined}
                  />
                  <p className={style['task-text']}>
                    {task.text.split('\n').map((line, i, arr) => (
                      <span key={i}>
                        {line}
                        {i < arr.length - 1 && <br />}
                      </span>
                    ))}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
